import { useState } from "react";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSTextField from "../../system-design/DSTextField";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSButton from "../../system-design/DSButton";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import serialsClient, {
  GeneratedSerial,
  batchFileName,
  saveBlob,
} from "../../../api/serials";
import useGenerateBatchMutation from "../hooks/useGenerateBatchMutation";

type GenerateBatchModalProps = {
  show: boolean;
  onClose: () => void;
};

const DEFAULT_PREFIX = "5GP02";
const MAX_BATCH_SIZE = 2000;

// Recovery path: the batch is committed but its CSV never reached the
// bucket, so build an equivalent file from the serials the API returned.
const saveFallbackCsv = (batchName: string, serials: GeneratedSerial[]) => {
  const lines = [
    "friendly,serial,registerUrl",
    ...serials.map(
      (s) =>
        `${s.friendlyIdentifier},${s.serialNumber},https://klydoclock.com/register?device=${s.serialNumber}`,
    ),
  ].join("\r\n");
  saveBlob(new Blob([lines], { type: "text/csv" }), batchFileName(batchName));
};

const GenerateBatchModal = ({ show, onClose }: GenerateBatchModalProps) => {
  const [count, setCount] = useState<number>(100);
  const [prefix, setPrefix] = useState<string>(DEFAULT_PREFIX);
  const [confirmed, setConfirmed] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const { mutate, data, isPending, isSuccess, error, reset } =
    useGenerateBatchMutation();

  const close = () => {
    reset();
    setConfirmed(false);
    setCount(100);
    setPrefix(DEFAULT_PREFIX);
    setDownloadError(false);
    onClose();
  };

  const handleGenerate = () => {
    mutate({ count, prefix: prefix.trim() || undefined });
  };

  const handleDownload = async () => {
    if (!data) return;
    setDownloadError(false);
    if (data.csvUploadFailed && data.serials) {
      saveFallbackCsv(data.batchName, data.serials);
      return;
    }
    try {
      await serialsClient.downloadBatch(data.batchName);
    } catch {
      setDownloadError(true);
    }
  };

  return (
    <DSDialog open={show} onClose={close} maxWidth="sm" fullWidth>
      <DSDialogTitle>Generate serial batch</DSDialogTitle>
      <DSDialogContent>
        {!isSuccess && (
          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
          >
            <DSTextField
              label="Number of serials"
              type="number"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              inputProps={{ min: 1, max: MAX_BATCH_SIZE }}
              fullWidth
            />
            <DSTextField
              label="Serial prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              fullWidth
            />
            <DSBox
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 1,
                backgroundColor: theme.palette.warning.light,
                color: theme.palette.warning.contrastText,
              })}
            >
              <DSTypography variant="body2">
                This creates {count} real Firebase users and machine documents
                in production, and writes a new batch to the serials bucket.
                Each friendly identifier is checked against machines_latest and
                the registry, so duplicates cannot be created.
              </DSTypography>
            </DSBox>
            {!confirmed ? (
              <DSPrimaryButton
                color="warning"
                onClick={() => setConfirmed(true)}
                disabled={!count || count < 1 || count > MAX_BATCH_SIZE}
              >
                I understand — continue
              </DSPrimaryButton>
            ) : (
              <DSBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DSPrimaryButton onClick={handleGenerate} disabled={isPending}>
                  {isPending ? "Generating…" : `Generate ${count}`}
                </DSPrimaryButton>
                {isPending && <DSCircularProgress size={22} />}
              </DSBox>
            )}
            {error && (
              <DSTypography variant="body2" color="error">
                {(error as Error).message || "Generation failed"}
              </DSTypography>
            )}
          </DSBox>
        )}

        {isSuccess && data && (
          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}
          >
            <DSTypography variant="h6">
              Created {data.created} of {data.requested} serials
            </DSTypography>
            <DSTypography variant="body2">
              Batch file: <strong>{batchFileName(data.batchName)}</strong>
            </DSTypography>
            {data.failed.length > 0 && (
              <DSTypography variant="body2" color="error">
                {data.failed.length} failed to provision (see logs).
              </DSTypography>
            )}
            {data.csvUploadFailed && (
              <DSTypography variant="body2" color="error">
                The batch CSV could not be written to the bucket. Download it
                here now — this dialog is the only place it can be recovered
                from.
              </DSTypography>
            )}
            <DSPrimaryButton onClick={handleDownload}>
              Download {batchFileName(data.batchName)}
            </DSPrimaryButton>
            {downloadError && (
              <DSTypography variant="body2" color="error">
                Download failed — try again from the Batches list.
              </DSTypography>
            )}
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSButton onClick={close}>{isSuccess ? "Done" : "Cancel"}</DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default GenerateBatchModal;
